import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from '@aimsos/auth';

import { DomainIndexRedirect } from './components';
import { UnifiedProfilesProviderWrapper } from './context';
import {
  CreateRecordPage,
  EntityListPage,
  ProfileTabPage,
  UnifiedProfilePage,
} from './pages';
import { OVERVIEW_TAB } from './constants/profileTabs.js';

/**
 * Domain root for Unified Profiles. Owns the internal routes and the provider.
 *
 * Paths are RELATIVE to the parent route that mounts this component:
 *   - Shell:      <Route path="unified-profiles/*">
 *   - Standalone: <Route path="unified-profiles/*"> in main.jsx
 *
 * Two screens: the Records LIST and a record's PROFILE. The list is the domain
 * index (every type together) and `/:entityType` (one type), so a type stays
 * linkable. The profile is addressed by type AND id, and its tabs are child
 * routes rather than local state so Back, Forward and a pasted URL all behave.
 */
function UnifiedProfiles() {
  return (
    <UnifiedProfilesProviderWrapper>
      <Routes>
        <Route
          index
          element={
            <ProtectedRoute>
              <EntityListPage />
            </ProtectedRoute>
          }
        />
        {/* Declared before `:entityType`, which would otherwise swallow it as a
            type called "new". React Router ranks a static segment higher anyway;
            the comment is for the next reader, who would not know that. */}
        <Route
          path="new"
          element={
            <ProtectedRoute>
              <CreateRecordPage />
            </ProtectedRoute>
          }
        />
        <Route
          path=":entityType"
          element={
            <ProtectedRoute>
              <EntityListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path=":entityType/:entityId"
          element={
            <ProtectedRoute>
              <UnifiedProfilePage />
            </ProtectedRoute>
          }
        >
          {/* ONE dynamic route. Which tabs exist, what they are called and what
              they render is decided by the tabs endpoint, per entity type — the
              frontend keeps no list. Overview is the single exception: it is the
              screen's invariant and the landing tab. The splat lets a tab
              own child routes of its own (Intelligence: traits / insights). */}
          <Route index element={<Navigate to={OVERVIEW_TAB.id} replace />} />
          <Route path=":tabId/*" element={<ProfileTabPage />} />
        </Route>
        <Route path="*" element={<DomainIndexRedirect />} />
      </Routes>
    </UnifiedProfilesProviderWrapper>
  );
}

export default UnifiedProfiles;
